import { useId, useState } from "react";
import type { KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";

import { useLocations } from "@/api/queries";
import type { components } from "@/api/schema";
import { FormField } from "@/components/ui";
import { useSettled } from "@/lib/useSettled";
import { fold } from "./model";

/**
 * *Ciudad* on the capture window: one box that is both the search and the
 * choice — a select with a search in it.
 *
 * It was two controls, a search box that refilled a `<select>` beside it, and
 * reception read them as two questions. The catalogue is some two and a half
 * thousand municipalities, so it still searches on the server; what changed is
 * that the matches open under the box they were typed in (the pattern of
 * `actividades/CompanyPicker`).
 *
 * While the box is in use it holds what is being typed and shows the chosen
 * city behind it, as its placeholder; left alone, it holds the city. So a
 * search that is abandoned changes nothing, and what the box says is always
 * what will be sent.
 */

type Location = components["schemas"]["Location"];

/** A city as the form holds it: the id to send, and the name to show. */
export type PickedCity = { id: number; label: string };

/** The same floor `useLocations` has: one letter is half the catalogue. */
const MIN_LETTERS = 2;

/**
 * Municipalities that start with what was typed come first: "León" is the
 * city in Guanajuato before it is the fifty-one in Nuevo León.
 */
function ranked(results: readonly Location[], term: string): Location[] {
  const wanted = fold(term);
  const rank = (location: Location) =>
    fold(location.municipality).startsWith(wanted) ? 0 : 1;
  return [...results].sort((a, b) => rank(a) - rank(b));
}

function Icon({ path }: { path: string }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={path} />
    </svg>
  );
}

export function CityPicker({
  label,
  value,
  onChange,
}: {
  label: string;
  value: PickedCity | null;
  onChange: (city: PickedCity | null) => void;
}) {
  const { t } = useTranslation();
  const listId = useId();
  /** What is being typed, or `null` while the box is not in use. */
  const [query, setQuery] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const term = (query ?? "").trim();
  const searching = term.length >= MIN_LETTERS;
  const settled = useSettled(searching ? term : "");
  const locations = useLocations(settled);
  const shown =
    searching && settled ? ranked(locations.data?.results ?? [], settled) : [];
  const stale = settled !== term || locations.isPending || locations.isPlaceholderData;
  const activeCity = shown[active];

  function pick(location: Location) {
    onChange({ id: location.id, label: location.short_city });
    setQuery("");
    setOpen(false);
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Backspace" && query === "" && value) {
      // Nothing left to delete but the city itself, as in a select.
      onChange(null);
    } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
      if (!open || shown.length === 0) return;
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActive((index) => (index + step + shown.length) % shown.length);
    } else if (event.key === "Enter" && open) {
      event.preventDefault();
      if (activeCity) pick(activeCity);
    } else if (event.key === "Escape" && open) {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
    }
  }

  const status = !searching
    ? t("reception.city.hint")
    : stale && shown.length === 0
      ? t("reception.city.searching")
      : locations.isError
        ? t("errors.locations")
        : shown.length === 0
          ? t("reception.city.noMatches", { term })
          : null;

  return (
    <FormField label={label}>
      {(id) => (
        <div className="relative">
          <input
            id={id}
            type="text"
            role="combobox"
            autoComplete="off"
            aria-autocomplete="list"
            aria-expanded={open}
            {...(open && shown.length > 0 ? { "aria-controls": listId } : {})}
            {...(open && activeCity
              ? { "aria-activedescendant": `${listId}-${activeCity.id}` }
              : {})}
            // The chosen city reads as the value it is, not as a hint.
            className={`input pr-9 ${value ? "placeholder:text-ink placeholder:opacity-100" : ""}`}
            placeholder={value?.label ?? t("reception.city.placeholder")}
            value={query ?? value?.label ?? ""}
            onChange={(event) => {
              setQuery(event.target.value);
              setActive(0);
              setOpen(true);
            }}
            onFocus={() => {
              setQuery("");
              setActive(0);
              setOpen(true);
            }}
            onBlur={() => {
              setQuery(null);
              setOpen(false);
            }}
            onKeyDown={onKeyDown}
          />

          {value ? (
            <button
              type="button"
              className="absolute top-1/2 right-1.5 flex h-6 w-6 -translate-y-1/2 items-center justify-center text-mute-600 hover:text-ink"
              aria-label={t("reception.city.clear")}
              // Keep the box as it is: a blur here would close the list first.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => onChange(null)}
            >
              <Icon path="M18 6 6 18M6 6l12 12" />
            </button>
          ) : (
            <span className="pointer-events-none absolute top-1/2 right-2.5 -translate-y-1/2 text-mute-600">
              <Icon path="m6 9 6 6 6-6" />
            </span>
          )}

          {open ? (
            <div className="absolute inset-x-0 top-full z-20 mt-1 border-2 border-rule bg-ground">
              {status ? (
                <p className="mb-0 px-3 py-2 text-xs text-mute-600" aria-live="polite">
                  {status}
                </p>
              ) : null}
              {shown.length > 0 ? (
                <ul id={listId} role="listbox" className="max-h-64 overflow-y-auto">
                  {shown.map((location, index) => (
                    <li
                      key={location.id}
                      id={`${listId}-${location.id}`}
                      role="option"
                      aria-selected={index === active}
                      // The arrow keys can walk past the edge of the list.
                      ref={
                        index === active
                          ? (node) => node?.scrollIntoView?.({ block: "nearest" })
                          : undefined
                      }
                      className={`cursor-pointer border-b border-rule px-3 py-2 text-sm last:border-0 ${
                        index === active ? "bg-panel" : ""
                      }`}
                      // Keep the focus in the box, or its blur closes the list
                      // before the click can land on the option.
                      onMouseDown={(event) => event.preventDefault()}
                      onMouseEnter={() => setActive(index)}
                      onClick={() => pick(location)}
                    >
                      {location.short_city}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
        </div>
      )}
    </FormField>
  );
}
